import { useMemo, useState } from 'react';
import { C, ink } from '../lib/theme';
import type { EventType, Filters, LostEvent, Reason } from '../lib/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useEventList } from '../lib/useEventList';
import { EventList } from './EventList';
import { WaterfallChart, type WaterfallKey } from './charts/WaterfallChart';
import { Block, EmptyState, ErrorBanner, Skeleton } from './primitives';
import type { EventCardHandlers } from './EventCard';
import { useToast } from './Toast';
import { useFilters } from '../lib/useFilters';
import { ScaleModal, type Origin } from './ScaleModal';
import { IconCircleDashed, IconTrendDown, IconX } from './icons';

type Bucket = 'perda' | 'anulado' | 'vdi';
const ALL_BUCKETS: Bucket[] = ['perda', 'anulado', 'vdi'];
const ALL_TYPES: EventType[] = ['churn', 'contraction'];
const BUCKET_BY_WATERFALL_KEY: Record<Exclude<WaterfallKey, 'perda_bruta'>, Bucket> = {
  perda_confirmada: 'perda', anulado: 'anulado', vdi: 'vdi',
};

/**
 * Bucket de um evento confirmado — mesmo critério que `getConfirmadosKpis`
 * usa no backend (correção manda mesmo com motivo de regra também presente):
 * correção existente → Anulado; sem correção mas motivo de regra → VDI;
 * nenhum dos dois → Perda Confirmada. Calculado no cliente porque os campos
 * já vêm no payload de `listEvents` — não precisa de endpoint novo.
 */
function bucketOf(ev: LostEvent): Bucket {
  if (ev.corrected_event_type !== null) return 'anulado';
  if (ev.reason_is_exception === true) return 'vdi';
  return 'perda';
}

function effectiveType(ev: LostEvent): EventType {
  return ev.corrected_event_type ?? ev.event_type;
}

/**
 * Par de botões quadrados Churn/Contraction, encaixados na diagonal (cada um
 * ocupa um canto do container, se sobrepondo um pouco no meio) — pedido
 * explícito de layout, "bem encaixados na diagonal e bonitinhos". Cada um
 * filtra o tipo de evento na lista abaixo, independente dos buckets do
 * waterfall.
 */
function TypeDiagonalToggle({ active, onToggle }: { active: Set<EventType>; onToggle: (t: EventType) => void }) {
  const base = {
    position: 'absolute' as const, width: 34, height: 34, borderRadius: 11,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', font: 'inherit',
    transition: 'background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, transform 0.15s ease',
  };
  const churnOn = active.has('churn');
  const contractionOn = active.has('contraction');
  return (
    <div style={{ position: 'relative', width: 56, height: 56, flex: 'none' }}>
      <button
        type="button" onClick={() => onToggle('contraction')} title="Filtrar Contraction"
        style={{
          ...base, top: 22, left: 22, zIndex: contractionOn ? 2 : 1,
          border: `1.5px solid ${contractionOn ? C.amber : ink(0.14)}`,
          background: contractionOn ? 'rgba(245,165,36,0.18)' : ink(0.04),
          color: contractionOn ? C.amber : ink(0.32),
          opacity: contractionOn ? 1 : 0.7,
          boxShadow: contractionOn ? '0 0 14px -4px rgba(245,165,36,0.6)' : 'none',
        }}
      ><IconTrendDown size={15} /></button>
      <button
        type="button" onClick={() => onToggle('churn')} title="Filtrar Churn"
        style={{
          ...base, top: 0, left: 0, zIndex: churnOn ? 2 : 1,
          border: `1.5px solid ${churnOn ? C.churn : ink(0.14)}`,
          background: churnOn ? 'rgba(242,85,90,0.18)' : ink(0.04),
          color: churnOn ? C.churn : ink(0.32),
          opacity: churnOn ? 1 : 0.7,
          boxShadow: churnOn ? '0 0 14px -4px rgba(242,85,90,0.6)' : 'none',
        }}
      ><IconX size={14} /></button>
    </div>
  );
}

/**
 * Painel de Confirmados — substitui a antiga aba de topo (PRD §4).
 *
 * Por que deixou de ser aba: auditar a fila é a tarefa contínua do dia, e
 * conferir o que já foi validado é uma consulta pontual dentro dessa tarefa.
 * Trocar de aba custava perder a posição na fila e o foco do teclado; um
 * painel devolve o operador exatamente onde ele estava ao fechar.
 *
 * Redesenho 2026-09-14 (pedido explícito): os 4 tiles de resumo (Total/
 * Perda Confirmada/Anulado/VDI) saíram — o próprio waterfall virou o
 * cabeçalho principal do painel, sempre visível (não mais dentro de um
 * `Block` recolhível), e cada barra é o filtro (clicar apaga/acende a barra
 * e restringe a lista abaixo). Sem redundância: o waterfall já mostrava os
 * mesmos 4 números que os tiles, então virar filtro em vez de duplicar como
 * "resumo + filtro separado" é estritamente menos UI pro mesmo dado.
 */
export function ConfirmadosPanel({ open, origin, onClose, filters, reasons, handlers }: {
  open: boolean;
  origin: Origin | null;
  onClose: () => void;
  filters: Filters;
  reasons: Reason[];
  handlers: Omit<EventCardHandlers, 'onAudit' | 'onPickReason' | 'onSetCorrection' | 'onClearCorrection'>;
}) {
  const toast = useToast();
  const { clearAll } = useFilters();
  const key = JSON.stringify(filters);
  const [activeBuckets, setActiveBuckets] = useState<Set<Bucket>>(new Set(ALL_BUCKETS));
  const [activeTypes, setActiveTypes] = useState<Set<EventType>>(new Set(ALL_TYPES));

  const waterfall = useAsync(
    () => (open ? api.waterfall({ ...filters, audit_status: 'real_lost' }) : Promise.resolve([])),
    [key, open],
  );
  const list = useEventList(filters, 'confirmados', reasons, open);
  const filteredItems = useMemo(
    () => list.items.filter((ev) => activeBuckets.has(bucketOf(ev)) && activeTypes.has(effectiveType(ev))),
    [list.items, activeBuckets, activeTypes],
  );

  const toggleBucket = (b: Bucket) => setActiveBuckets((prev) => {
    const next = new Set(prev);
    if (next.has(b)) next.delete(b); else next.add(b);
    return next;
  });
  const toggleType = (t: EventType) => setActiveTypes((prev) => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });
  // Clique na barra "Bruto" é o toggle mestre dos 3 buckets (equivalente ao
  // antigo tile "Total") — ela não é um bucket próprio, é a soma dos outros 3.
  const onToggleWaterfallBar = (waterfallKey: WaterfallKey) => {
    if (waterfallKey === 'perda_bruta') {
      setActiveBuckets(activeBuckets.size === ALL_BUCKETS.length ? new Set() : new Set(ALL_BUCKETS));
      return;
    }
    toggleBucket(BUCKET_BY_WATERFALL_KEY[waterfallKey]);
  };
  const totalChecked = activeBuckets.size === ALL_BUCKETS.length;
  const waterfallActiveKeys = useMemo(() => {
    const s = new Set<WaterfallKey>();
    if (totalChecked) s.add('perda_bruta');
    activeBuckets.forEach((b) => {
      if (b === 'perda') s.add('perda_confirmada');
      else s.add(b);
    });
    return s;
  }, [activeBuckets, totalChecked]);

  const fullHandlers: EventCardHandlers = {
    ...handlers,
    onAudit: (ev) => list.toggleAudit(ev, (msg) => toast(msg, 'error')),
    onPickReason: (ev, picked) => {
      const reason = picked[0] ?? null;
      list.setReason(ev, picked, (msg) => toast(msg, 'error'));
      if (reason?.is_exception) {
        toast('Esse evento vai ser excluído do total de Perda Confirmada.', 'info');
      }
    },
    onSetCorrection: (ev, body) => list.setCorrection(ev, body, (msg) => toast(msg, 'error')),
    onClearCorrection: (ev) => list.clearCorrection(ev, (msg) => toast(msg, 'error')),
  };

  const noFilterSelected = activeBuckets.size === 0 || activeTypes.size === 0;

  return (
    <ScaleModal
      open={open} origin={origin} onClose={onClose}
      title="Auditor Details"
      width={1040}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Cabeçalho principal — era um Block recolhível, agora é a primeira
            coisa que a tela mostra, sempre aberta. As 4 barras são o filtro:
            clique liga/desliga, a cor apaga (cinza) quando desligado. */}
        <div style={{
          position: 'relative', borderRadius: 20, padding: '20px 22px 18px',
          background: ink(0.035), border: `1px solid ${ink(0.1)}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16.5, fontWeight: 800, letterSpacing: '-0.012em' }}>
                Bruto → Anulado → VDI → Confirmado
              </div>
              <div style={{ fontSize: 12, color: ink(0.45), marginTop: 3 }}>
                Clique numa barra ou no tipo pra filtrar a lista abaixo
              </div>
            </div>
            <TypeDiagonalToggle active={activeTypes} onToggle={toggleType} />
          </div>

          <div style={{ height: 230, marginTop: 6 }}>
            {waterfall.loading ? <Skeleton height={230} radius={14} />
              : waterfall.error ? <ErrorBanner message={waterfall.error} onRetry={waterfall.reload} />
              : (
                <WaterfallChart
                  steps={waterfall.data ?? []}
                  activeKeys={waterfallActiveKeys}
                  onToggle={onToggleWaterfallBar}
                />
              )}
          </div>
        </div>

        <Block
          size="lg"
          title="Eventos confirmados"
          count={list.loading ? undefined : String(filteredItems.length)}
        >
          <EventList
            items={filteredItems} loading={list.loading} loadingMore={list.loadingMore}
            error={list.error} hasMore={list.hasMore} busyIds={list.busyIds}
            variant="confirmados" reasons={reasons} filters={filters} handlers={fullHandlers}
            onLoadMore={list.loadMore} onRetry={list.reload}
            empty={
              noFilterSelected ? (
                <EmptyState
                  tone="neutral" icon={<IconCircleDashed size={22} />} title="Nenhum filtro selecionado"
                  text="Marque pelo menos uma barra do waterfall e um tipo (Churn/Contraction) pra ver os eventos."
                  action={
                    <button
                      type="button"
                      onClick={() => { setActiveBuckets(new Set(ALL_BUCKETS)); setActiveTypes(new Set(ALL_TYPES)); }}
                      style={{
                        marginTop: 6, border: `1px solid ${ink(0.18)}`, background: ink(0.06), color: C.fg,
                        cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 650,
                        padding: '9px 18px', borderRadius: 11,
                      }}
                    >Selecionar tudo</button>
                  }
                />
              ) : (
                <EmptyState
                  tone="neutral" icon={<IconCircleDashed size={22} />} title="Nada confirmado ainda"
                  text="Nenhum evento bate com os filtros atuais. Eventos aparecem aqui depois de auditados na fila."
                  action={
                    <button type="button" onClick={clearAll} style={{
                      marginTop: 6, border: `1px solid ${ink(0.18)}`, background: ink(0.06), color: C.fg,
                      cursor: 'pointer', font: 'inherit', fontSize: 13, fontWeight: 650,
                      padding: '9px 18px', borderRadius: 11,
                    }}>Limpar filtros</button>
                  }
                />
              )
            }
          />
        </Block>
      </div>
    </ScaleModal>
  );
}
